// eslint-disable-next-line no-unused-vars
import { createSlice, createAsyncThunk, current } from '@reduxjs/toolkit';
import { requestStarted, requestFinished } from '~/stores/redux/common/loadingSlice';
import playerIconApi from './playerIcon.api';
import { alertSwal } from '~/utils/helpers';
import { handleThunkException } from '~/services/thunk.service';

export const fetchPlayerIconApi = createAsyncThunk(
    'itemSaga/playerIcon/fetchPlayerIconApi',
    async (navigate, { dispatch, rejectWithValue }) => {
        try {
            dispatch(requestStarted());
            const data = await playerIconApi.index();
            if (data) {
                dispatch(requestFinished());
                return data;
            }
        } catch (error) {
            handleThunkException(error, dispatch, navigate);

            return rejectWithValue([]);
        }
    },
);

export const togglePlayerIconPublishedStateApi = createAsyncThunk(
    'itemSaga/playerIcon/togglePlayerIconPublishedStateApi',
    async (row, { dispatch, rejectWithValue }) => {
        try {
            await playerIconApi.update(row.id, {is_ready: !row.is_ready});
            return row.id;
        } catch (error) {
            handleThunkException(error, dispatch);

            return rejectWithValue([]);
        }
    },
);

export const createPlayerIconApi = createAsyncThunk(
    'itemSaga/playerIcon/createPlayerIconApi',
    async (navigate, { dispatch, rejectWithValue }) => {
        try {
            dispatch(requestStarted());
            const { data } = await playerIconApi.create();

            return data;
        } catch (error) {
            handleThunkException(error, dispatch, navigate);

            return rejectWithValue();
        } finally {
            dispatch(requestFinished());
        }
    },
);

export const storePlayerIconApi = createAsyncThunk(
    'itemSaga/playerIcon/storePlayerIconApi',
    async (params, { dispatch, rejectWithValue }) => {
        const { navigate, values } = params;

        try {
            dispatch(requestStarted());
            const playerIcon = await playerIconApi.store(values);
            alertSwal('success', 'PlayerIcon created successfully!');

            return playerIcon;
        } catch (error) {
            handleThunkException(error, dispatch, navigate);

            return rejectWithValue();
        } finally {
            dispatch(requestFinished());
        }
    },
);

export const showPlayerIconApi = createAsyncThunk(
    'itemSaga/playerIcon/showPlayerIconApi',
    async (params, { dispatch, rejectWithValue }) => {
        const { id, navigate } = params;

        try {
            dispatch(requestStarted());
            const data = await playerIconApi.show(id);

            return data;
        } catch (error) {
            handleThunkException(error, dispatch, navigate);

            return rejectWithValue();
        } finally {
            dispatch(requestFinished());
        }
    },
);

export const updatePlayerIconApi = createAsyncThunk(
    'itemSaga/playerIcon/updatePlayerIconApi',
    async (params, { dispatch, getState, rejectWithValue }) => {
        const { navigate, id, values } = params;
        const state = getState();

        try {
            dispatch(requestStarted());
            await playerIconApi.update(id, values);
            alertSwal('success', 'PlayerIcon updated successfully!');

            const { ranks } = state.itemSaga.playerIconMetaData;
            const updatedRank = ranks.find((item) => item.id === values.rankId);

            return { values, updatedRank };
        } catch (error) {
            handleThunkException(error, dispatch, navigate);

            return rejectWithValue([]);
        } finally {
            dispatch(requestFinished());
        }
    },
);

export const deletePlayerIconApi = createAsyncThunk('itemSaga/playerIcon/deletePlayerIconApi', async (params, { dispatch }) => {
    const { navigate, id } = params;

    try {
        await playerIconApi.delete(id);

        return id;
    } catch (error) {
        handleThunkException(error, dispatch, navigate);
    }
});

export const playerIconSlice = createSlice({
    name: 'itemSaga/playerIcon',
    initialState: { rows: [], model: {}, editedModel: {}, pictureAttributeNames: [] }, //model is initialValues that are used for formik.
    reducers: {
        clearEditedPlayerIcon(state) {
            state.editedModel = {};
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchPlayerIconApi.fulfilled, (state, { payload }) => {
                return {
                    ...state,
                    rows: payload,
                };
            })
            .addCase(fetchPlayerIconApi.rejected, (state, { payload = [] }) => {
                state.rows = payload;
            });

        builder
            .addCase(togglePlayerIconPublishedStateApi.fulfilled, (state, { payload }) => {
                const updateRow = state.rows.find((row) => row.id === payload);
                updateRow.is_ready = !updateRow.is_ready;
            })
            .addCase(togglePlayerIconPublishedStateApi.rejected, () => {
                //do nothing so that state will remain intact and component will not re-render.
            });

        builder
            .addCase(createPlayerIconApi.fulfilled, (state, { payload }) => {
                state.model = payload.model;
                state.pictureAttributeNames = payload.pictureAttributeNames;
            })
            .addCase(createPlayerIconApi.rejected, () => {
                //
            });

        builder
            .addCase(storePlayerIconApi.fulfilled, (state) => {
                return {
                    ...state,
                    rows: [], //clear all rows so that api fetchAll will be called again in index page.
                };
            })
            .addCase(storePlayerIconApi.rejected, () => {
                //do nothing so that state will remain intact and component will not re-render.
            });

        builder
            .addCase(showPlayerIconApi.fulfilled, (state, { payload }) => {
                state.editedModel = payload;
            })
            .addCase(showPlayerIconApi.rejected, () => {
                //
            });

        builder
            .addCase(updatePlayerIconApi.fulfilled, (state, { payload }) => {
                state.editedModel = payload; //this is to make Update component re-render so that the checkDuplicateName() will have the latest `initialName`.

                if (state.rows.length === 0) return;

                const updatedRow = state.rows.find((row) => row.id === payload.values.id);
                updatedRow.name = payload.values.name;
                updatedRow.statPoint = payload.values.statPoint;
                updatedRow.rank = payload.updatedRank.name;
            })
            .addCase(updatePlayerIconApi.rejected, () => {
                //
            });

        builder.addCase(deletePlayerIconApi.fulfilled, (state, { payload }) => {
            return {
                ...state,
                rows: state.rows.filter(({ id }) => id !== payload),
            };
        });
    },
});

export const { clearEditedPlayerIcon } = playerIconSlice.actions;
